import * as XLSX from 'xlsx';
import type { TppCardRow, TppEventRow, TppSettings, TppSnapshot } from './tppLocalDb';
import { defaultTppSettings, nowIso } from './tppLocalDb';

export const TPP_XLSX_PATH = 'transit-pass-pool/transit-pass-pool.xlsx';

const EVENT_HEADERS = [
  'eventId',
  'cardId',
  'cardDisplayNumber',
  'physicalId',
  'eventKind',
  'outDate',
  'inDate',
  'departmentLabel',
  'borrowerName',
  'purpose',
  'place',
  'route',
  'amountYen',
  'amountYenDisplay',
  'runningBalance',
  'checkoutStatusLabel',
  'proofPresent',
  'proofFile',
  'createdAt',
] as const;

const CARD_HEADERS = ['id', 'displayNumber', 'physicalId', 'isActive', 'balance'] as const;

const SETTINGS_KEYS = [
  'poolKey',
  'displayPrefix',
  'displaySuffix',
  'isoCurrencyCode',
  'fractionDigits',
  'inputRoundingMode',
  'displayRoundingMode',
] as const;

function cell(value: string | number | boolean | null | undefined): string {
  if (value == null) return '';
  return String(value);
}

function cardBalance(cardId: number, events: TppEventRow[]): number {
  let sum = 0;
  for (const e of events) {
    if (e.cardId === cardId && e.amount != null) sum += e.amount;
  }
  const bal = Math.round(-sum * 100) / 100;
  return bal === 0 ? 0 : bal;
}

function cardById(snap: TppSnapshot): Map<number, TppCardRow> {
  return new Map(snap.cards.map((c) => [c.id, c]));
}

function amountYenDisplay(eventKind: string, amount: number | null): string {
  if (amount == null) return '';
  if (eventKind === 'charge') return cell(Math.abs(amount));
  return cell(amount);
}

function checkoutStatusLabel(status: 0 | 1): string {
  return status === 1 ? '貸出中' : '—';
}

function parseCheckoutStatus(label: string): 0 | 1 {
  const t = label.trim();
  return t === '貸出中' ? 1 : 0;
}

function parseBool(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  const t = String(v ?? '').trim().toLowerCase();
  return t === 'true' || t === '1' || t === 'yes' || t === 'はい' || t === '有効';
}

function parseNumberOrNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function parseDateOnly(v: unknown): string | null {
  if (v == null || v === '') return null;
  const t = String(v).trim();
  const datePart = /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : t.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  return datePart ?? null;
}

function sheetToRows(wb: XLSX.WorkBook, name: string): string[][] {
  const ws = wb.Sheets[name];
  if (!ws) return [];
  return XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, defval: '' }) as string[][];
}

function rowToMap(headers: readonly string[], row: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < headers.length; i++) {
    out[headers[i]!] = String(row[i] ?? '').trim();
  }
  return out;
}

export function buildTppWorkbookSheets(snap: TppSnapshot): Record<string, string[][]> {
  const cards = cardById(snap);
  const runningByCard = new Map<number, number>();

  const eventRows: string[][] = [[...EVENT_HEADERS]];
  const sortedEvents = [...snap.events].sort((a, b) => a.id - b.id);
  for (const e of sortedEvents) {
    const card = cards.get(e.cardId);
    let running = runningByCard.get(e.cardId) ?? 0;
    if (e.amount != null) running += e.amount;
    runningByCard.set(e.cardId, running);
    const balance = Math.round(-running * 100) / 100;
    eventRows.push([
      cell(e.id),
      cell(e.cardId),
      cell(card?.displayNumber ?? ''),
      card?.physicalId ?? '',
      e.eventKind,
      e.outDate,
      e.inDate ?? '',
      e.departmentLabel,
      e.borrowerName,
      e.purpose,
      e.place,
      e.route,
      cell(e.amount),
      amountYenDisplay(e.eventKind, e.amount),
      cell(balance),
      checkoutStatusLabel(e.checkoutStatus),
      e.proofStoredFilename ? 'あり' : '',
      e.proofStoredFilename ?? '',
      e.createdAt,
    ]);
  }

  const cardRows: string[][] = [[...CARD_HEADERS]];
  for (const c of [...snap.cards].sort((a, b) => a.displayNumber - b.displayNumber || a.id - b.id)) {
    cardRows.push([
      cell(c.id),
      cell(c.displayNumber),
      c.physicalId,
      c.isActive ? 'true' : 'false',
      cell(cardBalance(c.id, snap.events)),
    ]);
  }

  const settingsRows: string[][] = [
    ['key', 'value'],
    ...SETTINGS_KEYS.map((key) => {
      const value = snap.settings[key];
      return [key, cell(value as string | number | boolean | null | undefined)];
    }),
    ['revision', cell(snap.meta.revision)],
    ['updatedAt', snap.meta.updatedAt],
  ];

  return {
    Events: eventRows,
    Cards: cardRows,
    Settings: settingsRows,
  };
}

export function buildTppWorkbookBuffer(snap: TppSnapshot): ArrayBuffer {
  const sheets = buildTppWorkbookSheets(snap);
  const wb = XLSX.utils.book_new();
  for (const [name, rows] of Object.entries(sheets)) {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
  }
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
}

export function parseTppWorkbookBuffer(buffer: ArrayBuffer): {
  cards: TppCardRow[];
  events: TppEventRow[];
  settings: TppSettings;
} {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ts = nowIso();

  const settings = defaultTppSettings();
  const settingsRows = sheetToRows(wb, 'Settings');
  if (settingsRows.length > 1) {
    for (let i = 1; i < settingsRows.length; i++) {
      const row = settingsRows[i]!;
      const key = String(row[0] ?? '').trim();
      const value = String(row[1] ?? '').trim();
      if (key === 'poolKey') settings.poolKey = value || 'default';
      else if (key === 'displayPrefix') settings.displayPrefix = value;
      else if (key === 'displaySuffix') settings.displaySuffix = value;
      else if (key === 'isoCurrencyCode') settings.isoCurrencyCode = value || null;
      else if (key === 'fractionDigits') settings.fractionDigits = parseInt(value, 10) || 0;
      else if (key === 'inputRoundingMode') settings.inputRoundingMode = value || 'none';
      else if (key === 'displayRoundingMode') settings.displayRoundingMode = value || 'none';
    }
  }

  const poolKey = settings.poolKey;
  const cards: TppCardRow[] = [];
  const cardRows = sheetToRows(wb, 'Cards');
  if (cardRows.length > 1) {
    const headers = cardRows[0]!.map((h) => String(h).trim());
    const useHeaders = headers.some((h) => h === 'id') ? (headers as unknown as typeof CARD_HEADERS) : CARD_HEADERS;
    for (let i = 1; i < cardRows.length; i++) {
      const m = rowToMap(useHeaders, cardRows[i]!.map((c) => String(c ?? '')));
      const id = parseInt(m.id, 10);
      const displayNumber = parseInt(m.displayNumber, 10);
      if (!Number.isFinite(id) || !Number.isFinite(displayNumber)) continue;
      cards.push({
        id,
        poolKey,
        displayNumber,
        physicalId: m.physicalId ?? '',
        isActive: parseBool(m.isActive),
        createdAt: ts,
        updatedAt: ts,
      });
    }
  }

  const cardIdByDisplay = new Map(cards.map((c) => [c.displayNumber, c.id]));
  const events: TppEventRow[] = [];
  const eventRows = sheetToRows(wb, 'Events');
  if (eventRows.length > 1) {
    const headers = eventRows[0]!.map((h) => String(h).trim());
    const useHeaders = headers.some((h) => h === 'eventId')
      ? (headers as unknown as typeof EVENT_HEADERS)
      : EVENT_HEADERS;
    for (let i = 1; i < eventRows.length; i++) {
      const m = rowToMap(useHeaders, eventRows[i]!.map((c) => String(c ?? '')));
      const id = parseInt(m.eventId, 10);
      let cardId = parseInt(m.cardId, 10);
      if (!Number.isFinite(cardId)) {
        const dn = parseInt(m.cardDisplayNumber, 10);
        cardId = cardIdByDisplay.get(dn) ?? NaN;
      }
      if (!Number.isFinite(id) || !Number.isFinite(cardId)) continue;
      const eventKind = m.eventKind === 'charge' ? 'charge' : 'usage';
      const outDate = parseDateOnly(m.outDate);
      if (!outDate) continue;
      const inDate = parseDateOnly(m.inDate);
      const amount = parseNumberOrNull(m.amountYen);
      const checkoutStatus = parseCheckoutStatus(m.checkoutStatusLabel);
      const proofFile = m.proofFile?.trim() || null;
      events.push({
        id,
        cardId,
        eventKind,
        outDate,
        inDate,
        departmentLabel: m.departmentLabel ?? '',
        borrowerName: m.borrowerName ?? '',
        purpose: m.purpose ?? '',
        place: m.place ?? '',
        route: m.route ?? '',
        amount,
        checkoutStatus,
        proofStoredFilename: proofFile,
        proofMime: proofFile ? null : null,
        createdAt: m.createdAt?.trim() || ts,
        updatedAt: ts,
      });
    }
  }

  return { cards, events, settings };
}

export function buildTppWorkbookBlob(snap: TppSnapshot): Blob {
  const ab = buildTppWorkbookBuffer(snap);
  return new Blob([ab], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
